import { QueryClient, Query } from '@tanstack/react-query';

import { LOWEST_PRIORITY_SCORE } from '@app/constants/general';
import {
  FilterParam,
  QueryParam,
} from '@app/constants/job-query-filter-params';
import {
  PRIVATE_REDACTED,
  REDACTED_FROM_VIEWER,
} from '@app/constants/jobState';
import { QUERY_KEYS } from '@app/constants/service';
import { checkForCaseAccess } from '@app/helpers/cases';
import { isCaseDismissedForUser } from '@app/helpers/dismiss-helpers';
import { isContactPrefixEnabled } from '@app/helpers/featureFlags';
import { TAG_TYPES } from '@app/helpers/inbox-helpers';
import { isCaseSnoozedForUser } from '@app/helpers/snooze-helpers';
import { Group } from '@app/types/api/group';
import { Job } from '@app/types/api/job';
import { User } from '@app/types/api/user';
import { InboxFilters } from '@app/types/reducer-state';

import {
  getActionType,
  insertAndSortItemInList,
  modifyAndSortItemInList,
  removeItemFromListById,
} from './cacheHelpers';
import { shouldInboxConsumeUpdate } from './shouldInboxConsumeUpdate';
import UserModel from '../../models/user';

type ServerScopedFilters = InboxFilters & { totalLossStockNumber?: string };

/** Check whether a case is per-user suppressed (snoozed or dismissed). */
const isCaseSuppressedForUser = (job: Partial<Job>, userId: string): boolean =>
  isCaseSnoozedForUser(job?.snoozes, userId) ||
  isCaseDismissedForUser(job?.dismissals, userId);

/**
 * User-specific job fields that need to be preserved from existing cache
 * during live updates since they depend on user context/permissions
 */
const USER_SPECIFIC_FIELDS = ['isPinned'] as const;

/**
 * Display fields that must be preserved from cache when a live update
 * omits them (e.g. partial UPDATE_CASE after Release), to avoid
 * "Name Missing" / "Case Type Missing" in the inbox.
 */
const DISPLAY_PRESERVED_FIELDS = [
  'customerName',
  'lastMessage',
  'tags',
  'type',
  'caseNumber',
] as const;

const USER_SPECIFIC_EVENTS = ['read-note', 'toggle-pin'] as const;

/** The server resolves these, so a live update can't tell us whether a case matches. */
const SERVER_SCOPED_PARAMS = [
  QueryParam.SEARCH,
  QueryParam.JOB_IDS,
  FilterParam.TOTAL_LOSS_STOCK_NUMBER,
] as const;

const isServerScopedQuery = (filters?: ServerScopedFilters): boolean =>
  SERVER_SCOPED_PARAMS.some((param) => {
    const value = filters?.[param];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  });

/**
 * Build groups hierarchy object from React Query cache.
 * `jobMatchesWithAtLeastOneGroupMember` expects { groupId: { members, leads, subgroups } },
 * not the flat ID array stored on jobs.
 */
const getGroupsHierarchyFromCache = (
  queryClient: QueryClient,
): Record<string, Group> => {
  const groups = queryClient
    .getQueryCache()
    .getAll()
    .filter(
      (q) => Array.isArray(q.queryKey) && q.queryKey[0] === QUERY_KEYS.GROUPS,
    )
    .flatMap((query) => {
      const data = queryClient.getQueryData(query.queryKey) as {
        pages?: { groups?: Group[] }[];
      };
      return (data?.pages || []).flatMap((page) => page?.groups || []);
    });

  return Object.fromEntries(
    groups.map((g) => [g._id || g.id, g] as const).filter(([id]) => id),
  );
};

/**
 * Helper function to get all joblist queries from cache
 */
const getJoblistQueries = (queryClient: QueryClient) => {
  const cache = queryClient.getQueryCache();

  return cache
    .getAll()
    .filter(
      (query) =>
        Array.isArray(query.queryKey) && query.queryKey[0] === QUERY_KEYS.CASES,
    );
};

/**
 * Helper function to update joblist queries (paginated data)
 */
const updateJoblistQueries = (
  queries: Query[],
  queryClient: QueryClient,
  updater: (item: unknown) => unknown,
) => {
  queries.forEach((query) => {
    queryClient.setQueryData(query.queryKey, (oldData: unknown) => {
      const data = oldData as { pages?: unknown[][] };
      if (!data?.pages) {
        return oldData;
      }

      const updatedPages = data.pages.map((page: unknown[]) =>
        page.map((item: unknown) => updater(item)),
      );

      return { ...data, pages: updatedPages };
    });
  });
};

/**
 * Helper function to find an existing job in the cache by jobId
 * Searches through all joblist queries and returns the first matching job
 */
export const findExistingJobInCache = (
  queryClient: QueryClient,
  jobId: string,
): Job | null => {
  const joblistQueries = getJoblistQueries(queryClient);

  for (const query of joblistQueries) {
    const data = queryClient.getQueryData(query.queryKey) as {
      pages?: Job[][];
    };
    if (data?.pages) {
      for (const page of data.pages) {
        const found = page.find(
          (item) => item._id === jobId || item.id === jobId,
        );
        if (found) {
          return found;
        }
      }
    }
  }

  return null;
};

/**
 * Calculates the smartSort score for a live update based on the active inbox mode.
 * In unaddressed mode, hasUnaddressedMessage elevates priority.
 * In unread mode, hasUnreadMessages elevates priority.
 */
export const calculateLiveUpdateSmartSort = (
  job: Partial<Job>,
  userId: string,
  unaddressedEnabled: boolean,
  suppressionEnabled = false,
): number => {
  const serverScore = job?.smartSort ?? LOWEST_PRIORITY_SCORE;

  if (unaddressedEnabled) {
    const isSuppressed =
      suppressionEnabled && isCaseSuppressedForUser(job, userId);
    return job?.hasUnaddressedMessage && !isSuppressed
      ? Math.min(TAG_TYPES.hasUnaddressedMessage.sortRanking, serverScore)
      : serverScore;
  }

  const isUnread = job?.usersLastViewedMessage?.[userId]?.hasUnreadMessages;
  return isUnread
    ? Math.min(TAG_TYPES.hasUnreadMessages.sortRanking, serverScore)
    : serverScore;
};

/**
 * Simplified handler for updating a single case in the cache
 * Uses insert/update/delete logic based on filtering
 */
export const handleReactQueryJobCacheItem = (
  liveUpdateJob: Job,
  eventName: string,
  queryClient: QueryClient,
  userId: string,
  permissions: string[],
  unaddressedEnabled = false,
  suppressionEnabled = false,
  allowedCaseTypes: string[] = [],
): void => {
  if (!liveUpdateJob || !liveUpdateJob?._id) {
    return;
  }

  const jobId = liveUpdateJob?._id;
  const joblistQueries = getJoblistQueries(queryClient);

  // Find existing job in cache and check if update is needed
  const existingJob = findExistingJobInCache(queryClient, jobId);

  // Skip if live update is older than cache
  if (existingJob?.updatedAt && liveUpdateJob?.updatedAt) {
    const jobDate = new Date(liveUpdateJob.updatedAt).getTime();
    const existingDate = new Date(existingJob.updatedAt).getTime();
    if (jobDate < existingDate) {
      return;
    }
  }

  // Preserve user-specific fields and display fields when live update omits them
  const preservedFields: Record<string, unknown> = {};
  if (
    existingJob &&
    !USER_SPECIFIC_EVENTS.includes(
      eventName as (typeof USER_SPECIFIC_EVENTS)[number],
    )
  ) {
    const job = existingJob; // Capture for type narrowing
    USER_SPECIFIC_FIELDS.forEach((field) => {
      const value = job[field as keyof Job];
      if (value !== undefined) preservedFields[field] = value;
    });
    // Preserve display fields when live update omits them (avoids "Name Missing" / "Case Type Missing")
    DISPLAY_PRESERVED_FIELDS.forEach((field) => {
      const liveValue = liveUpdateJob[field as keyof Job];
      const existingValue = job[field as keyof Job];
      const liveMissing =
        liveValue === undefined ||
        liveValue === null ||
        (field === 'customerName' && (liveValue as string) === '') ||
        (field === 'tags' &&
          (!Array.isArray(liveValue) || !(liveValue as unknown[]).length));
      if (liveMissing && existingValue !== undefined) {
        preservedFields[field] = existingValue;
      }
    });
  }

  // Determine smartSort for this live update (unaddressed vs unread inbox mode)
  const smartSort = calculateLiveUpdateSmartSort(
    liveUpdateJob,
    userId,
    unaddressedEnabled,
    suppressionEnabled,
  );

  // Sanitize groups: server can send [id, id, null]; ensure cache never has nulls.
  // When the live update omits groups (common for partial updates like reassignment),
  // preserve the existing job's groups so group-filtered views aren't broken.
  const sanitizedGroups = Array.isArray(liveUpdateJob?.groups)
    ? liveUpdateJob.groups.filter((g): g is string => Boolean(g))
    : undefined;
  const effectiveGroups =
    sanitizedGroups ?? existingJob?.groups?.filter(Boolean);

  // Process user fields through User model for consistent formatting
  const processedJob = {
    ...liveUpdateJob,
    smartSort,
    ...preservedFields,
    ...(effectiveGroups !== undefined ? { groups: effectiveGroups } : {}),
    ...(liveUpdateJob.customer
      ? { customer: new UserModel(liveUpdateJob.customer) }
      : {}),
  };

  if (liveUpdateJob.isPrivate && liveUpdateJob.privacy === PRIVATE_REDACTED) {
    const hasAccess: boolean = checkForCaseAccess(
      liveUpdateJob,
      userId,
      permissions,
    );
    if (!hasAccess) {
      processedJob.state = REDACTED_FROM_VIEWER;
      processedJob.lastMessage = {
        sentAt: processedJob.lastMessage?.sentAt,
        body: 'This case is marked as private.',
      };
      processedJob.customerName = 'Private Case';
    }
  }

  // Get and cache live update groups (use effective groups so we never lose them)
  const liveUpdateGroupsQueryKey = [QUERY_KEYS.LIVE_UPDATE_GROUPS, jobId];
  let liveUpdateGroups = effectiveGroups?.length ? effectiveGroups : undefined;
  if (!liveUpdateGroups?.length) {
    liveUpdateGroups = queryClient.getQueryData(liveUpdateGroupsQueryKey);
  } else {
    queryClient.setQueryData(liveUpdateGroupsQueryKey, liveUpdateGroups);
  }

  const groupsHierarchy = getGroupsHierarchyFromCache(queryClient);

  joblistQueries.forEach((query) => {
    const filters = query.queryKey[1] as InboxFilters;
    const queryData: { pages?: Job[][] } | undefined = queryClient.getQueryData(
      query.queryKey,
    );

    // Skip queries without cache - they'll be fetched when needed
    if (!queryData?.pages) {
      return;
    }

    const cachedJob = queryData.pages
      ?.flat()
      .find((item) => item._id === jobId);

    // Determine if job matches current filters for this query
    // Use the groups hierarchy (keyed by groupId with members/leads) so
    // jobMatchesWithAtLeastOneGroupMember can check operator membership.
    const filtersMatchParams = {
      filters,
      job: processedJob,
      groups:
        Object.keys(groupsHierarchy).length > 0
          ? groupsHierarchy
          : processedJob.groups || [],
      eventName: eventName,
      cachedJob,
      liveUpdateGroups: liveUpdateGroups,
      allowedCaseTypes: allowedCaseTypes,
    };

    const filtersMatch = shouldInboxConsumeUpdate(filtersMatchParams, userId);

    const action = getActionType(filtersMatch, cachedJob);

    if (!action) {
      return;
    }

    if (action === 'insert' && isServerScopedQuery(filters)) {
      return;
    }

    if (action === 'update' && eventName !== 'toggle-pin') {
      processedJob.isPinned = cachedJob?.isPinned;
    }

    queryClient.setQueryData(query.queryKey, (oldData: unknown) => {
      const data = oldData as { pages?: Job[][] };
      if (!data?.pages) return oldData;

      const casesToSort = data.pages.flat();

      const clonedCases = {
        insert: () =>
          insertAndSortItemInList(
            casesToSort,
            processedJob,
            filters.order,
            filters.sort,
          ),
        update: () =>
          modifyAndSortItemInList(
            casesToSort,
            processedJob,
            filters.order,
            filters.sort,
          ),
        delete: () => removeItemFromListById(casesToSort, processedJob),
      }[action]();

      const pageSize = casesToSort.length || 1;
      const pages = Array.from(
        { length: Math.ceil(clonedCases.length / pageSize) },
        (_, i) => clonedCases.slice(i * pageSize, (i + 1) * pageSize),
      );

      return { ...data, pages };
    });
  });
};

/**
 * Removes closed cases from open-cases query caches by their IDs.
 * Only affects queries filtering for open cases (isOpen === true),
 * leaving closed-cases views, search results, and unfiltered views intact.
 * Used after bulk close to immediately reflect changes
 * without waiting for websocket live updates.
 */
export const removeCasesFromCache = (
  queryClient: QueryClient,
  jobIds: string[],
): void => {
  if (!jobIds?.length) return;

  const jobIdSet = new Set(jobIds);
  const joblistQueries = getJoblistQueries(queryClient);

  joblistQueries.forEach((query) => {
    const filters = query.queryKey[1] as InboxFilters & { isOpen?: boolean };

    // Only remove from queries that filter for open cases
    if (filters?.isOpen !== true) return;

    const queryData: { pages?: Job[][] } | undefined = queryClient.getQueryData(
      query.queryKey,
    );

    if (!queryData?.pages) return;

    const hasMatchingJobs = queryData.pages
      .flat()
      .some((job) => jobIdSet.has(job._id));

    if (!hasMatchingJobs) return;

    queryClient.setQueryData(query.queryKey, (oldData: unknown) => {
      const data = oldData as { pages?: Job[][] };
      if (!data?.pages) return oldData;

      const filtered = data.pages
        .flat()
        .filter((job) => !jobIdSet.has(job._id));
      const pageSize = data.pages[0]?.length || filtered.length || 1;
      const pages = filtered.length
        ? Array.from(
            { length: Math.ceil(filtered.length / pageSize) },
            (_, i) => filtered.slice(i * pageSize, (i + 1) * pageSize),
          )
        : [[]];

      return { ...data, pages };
    });
  });
};

/**
 * Finds a job in the cache and updates its unreadNoteMentions to mark a note as read
 * Removes the userId from unreadNoteMentions and updates all relevant cache queries
 * IMPORTANT: This function is only needed because we have not updated activeJob to be the same as the job in the cache
 */
export const findAndUpdateNoteReadInCache = (
  jobId: string,
  eventName: string,
  queryClient: QueryClient,
  userId: string,
  permissions: string[],
  unaddressedEnabled = false,
  suppressionEnabled = false,
): void => {
  if (!jobId) {
    return;
  }

  // Find existing job in cache
  const existingJob = findExistingJobInCache(queryClient, jobId);
  if (!existingJob) {
    return;
  }

  // Create updated job with userId removed from unreadNoteMentions
  const updatedUnreadNoteMentions = {
    ...(existingJob.unreadNoteMentions || {}),
  };
  delete updatedUnreadNoteMentions[userId];

  const updatedJob: Job = {
    ...existingJob,
    unreadNoteMentions: updatedUnreadNoteMentions,
  };

  // Use existing handler to update cache
  handleReactQueryJobCacheItem(
    updatedJob,
    eventName,
    queryClient,
    userId,
    permissions,
    unaddressedEnabled,
    suppressionEnabled,
  );
};

/**
 * Simplified handler for updating customer names in the cache
 * Directly updates customer names in all joblist queries
 */
export const handleReactQueryJobCacheCustomerNames = (
  customer: User,
  queryClient: QueryClient,
): void => {
  const customerRecord = customer as Record<string, unknown>;
  const customerId = customerRecord?.id || customerRecord?._id;
  if (!customerId) {
    return;
  }

  const profile = customerRecord?.profile as Record<string, unknown>;
  const contactPrefix =
    isContactPrefixEnabled() && profile?.contactPrefix
      ? `${profile.contactPrefix} `
      : '';
  const fullName =
    profile?.firstName && profile?.lastName
      ? `${profile.firstName} ${profile.lastName}`
      : profile?.firstName || profile?.lastName || '';
  const customerName = `${contactPrefix}${fullName}`.trim();

  const joblistQueries = getJoblistQueries(queryClient);

  updateJoblistQueries(joblistQueries, queryClient, (item) => {
    const jobItem = item as Record<string, unknown>;
    if (
      jobItem.customerId === customerId &&
      jobItem.customerName !== customerName
    ) {
      return { ...jobItem, customerName };
    }
    return item;
  });
};
